/** @jsxImportSource @emotion/react */

import Notabene, {
  VASPSearchControl,
  TransactionOptions,
  ProofTypes,
  TransactionResponse,
  Deposit,
} from '@notabene/javascript-sdk';
import { removeScaleFromMoney } from '@quickbit/qb-design-system';
import { useAsync } from 'react-use';
import { environment } from 'utils/environmentVariables';
import { OriginatorInfo } from './api';

interface TravelRuleOriginatorWidgetProps {
  init: () => Promise<OriginatorInfo>;
  mountOnElementId: string;
  onCompletion?: (data: TransactionResponse<Deposit>) => void;
  onNotValid?: () => void;
  brand?: string | null;
}

export const useTravelRuleOriginatorWidget = ({
  init,
  mountOnElementId,
  brand,
  onCompletion,
  onNotValid,
}: TravelRuleOriginatorWidgetProps) => {
  const { error, loading } = useAsync(async () => {
    const {
      amount,
      asset,
      customerToken,
      notabeneTransactionId,
      sourceAddress,
    } = await init();
    // 1. Create Notabene instance
    const notabene = new Notabene({
      nodeUrl: environment.REACT_APP_NOTABENE_NODE_URL,
      authToken: customerToken,
      theme: brand
        ? {
            mode: 'light',
            logo: 'https://leikur.com/assets/images/leikur-logo.svg',
            primaryColor: '#4c8ab3',
            secondaryColor: '#4c8ab3',
            fontFamily: 'sans-serif', // Google Fonts Supported
          }
        : undefined,
    });

    const options: TransactionOptions = {
      fields: {
        legalPerson: {
          lei: false,
        },
        naturalPerson: {
          placeOfBirth: false,
          dateOfBirth: false,
          geographicAddress: false,
          countryOfResidence: false,
        },
      },
      vasps: {
        addUnknown: true,
        searchable: [
          'allowed' as VASPSearchControl,
          'pending' as VASPSearchControl,
        ],
      },
      proofs: {
        fallbacks: [ProofTypes.SelfDeclaration],
      },
    };

    // 2. Create and mount deposit component
    // TODO: remove hardcoded values
    const deposit = notabene.createDepositAssist(
      {
        asset: asset,
        amountDecimal: removeScaleFromMoney(amount),
        source: sourceAddress,
        transactionId: notabeneTransactionId ?? undefined,
      },
      options
    );
    deposit.mount(`#${mountOnElementId}`);

    void deposit.completion().then((result) => {
      if (result.valid) {
        onCompletion && onCompletion(result);
      } else {
        onNotValid && onNotValid();
      }
    });
  });

  return { error, loading };
};
